import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, Users, FileCheck, FolderOpen, RefreshCw, UserPlus, Search, ChevronDown, ChevronUp, Download, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Farb-Themes pro Ordner
const FARBEN = {
  sage:       { bg: "bg-[#E9EFE7]", border: "border-[#BFD0BF]", text: "text-[#3F5540]", accent: "bg-[#5F7960]", pill: "bg-[#5F7960]" },
  coral:      { bg: "bg-[#FBEBE2]", border: "border-[#EBBBA6]", text: "text-[#8A3B22]", accent: "bg-[#C15B3C]", pill: "bg-[#C15B3C]" },
  terracotta: { bg: "bg-[#F6E4DF]", border: "border-[#E3B6AB]", text: "text-[#8A2F1F]", accent: "bg-[#8A2F1F]", pill: "bg-[#8A2F1F]" },
};

export default function AZAVOrdner() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedDocs, setExpandedDocs] = useState({}); // {kurs_id: bool}
  const [zuordnenDialog, setZuordnenDialog] = useState(null); // { ordner }
  const [ohneKurs, setOhneKurs] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [assigning, setAssigning] = useState(null);
  const [logExportDialog, setLogExportDialog] = useState(null); // { ordner }
  const [logVon, setLogVon] = useState("");
  const [logBis, setLogBis] = useState("");
  const [logStart, setLogStart] = useState("");
  const [logDozent, setLogDozent] = useState("");
  const [logGenerating, setLogGenerating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/azav/ordner-uebersicht`);
      setData(r.data);
    } catch (e) {
      toast.error("AZAV-Ordner konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openZuordnen = async (ordner) => {
    try {
      const r = await axios.get(`${API}/azav-mappe/ohne-kurs?nur_bg=true`);
      setOhneKurs(r.data?.teilnehmer || []);
      setSearchQ("");
      setZuordnenDialog({ ordner });
    } catch (e) {
      toast.error("Teilnehmerliste konnte nicht geladen werden");
    }
  };

  const zuordnen = async (tn, ordner) => {
    setAssigning(tn.id);
    try {
      await axios.post(`${API}/azav-mappe/kurs-zuordnen/${tn.id}`, {
        kurs_name: ordner.name,
        kurs_id: ordner.kurs_id,
        massnahme_id: ordner.massnahmenummer,
      });
      toast.success(`${tn.vorname} ${tn.nachname} → ${ordner.name}`);
      // Aus ohneKurs entfernen + refresh
      setOhneKurs(prev => prev.filter(t => t.id !== tn.id));
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen");
    } finally {
      setAssigning(null);
    }
  };

  const downloadDoc = async (code) => {
    try {
      const res = await axios.get(`${API}/qm-dokumente/${code}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`${code} konnte nicht heruntergeladen werden`);
    }
  };

  const openLogExport = (ordner) => {
    // Default: gesamter Kurszeitraum
    setLogVon("");
    setLogBis("");
    setLogStart("");
    setLogDozent("");
    setLogExportDialog({ ordner });
  };

  const generateAnwesenheitsLog = async () => {
    if (!logExportDialog) return;
    setLogGenerating(true);
    try {
      const params = new URLSearchParams({ kurs_id: logExportDialog.ordner.kurs_id });
      if (logVon) params.set("von", logVon);
      if (logBis) params.set("bis", logBis);
      if (logStart) params.set("start", logStart);
      if (logDozent) params.set("dozent", logDozent);
      const res = await axios.get(`${API}/azav/anwesenheits-log-pdf?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const von = logVon || "gesamt";
      const bis = logBis || "gesamt";
      a.download = `Anwesenheits-Auszug_${logExportDialog.ordner.kurs_id}_${von}_bis_${bis}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Anwesenheits-Auszug erzeugt");
      setLogExportDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anwesenheits-Auszug konnte nicht erzeugt werden");
    } finally {
      setLogGenerating(false);
    }
  };

  const filteredOhneKurs = ohneKurs.filter(t => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return (`${t.vorname} ${t.nachname} ${t.email}`).toLowerCase().includes(q);
  });

  if (loading || !data) {
    return <div className="p-8"><RefreshCw className="animate-spin inline mr-2" size={16}/> AZAV-Ordner werden geladen…</div>;
  }

  return (
    <div className="page-container" data-testid="azav-ordner-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>AZAV-Ordner</h1>
          <p className="text-sm text-[#78716c] mt-1">
            Übersicht der 3 zugelassenen AZAV-Maßnahmen · Bildungsgutschein-Teilnehmer &amp; Pflichtdokumente
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="azav-ordner-refresh">
          <RefreshCw size={16} className="mr-2" /> Aktualisieren
        </Button>
      </div>

      {/* 3 Ordner-Karten */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {data.ordner.map((o) => {
          const theme = FARBEN[o.farbe] || FARBEN.sage;
          const totalDocs = o.docs_status.traeger_total + o.docs_status.kurs_total;
          const okDocs = o.docs_status.traeger_ok + o.docs_status.kurs_ok;
          const docPct = totalDocs > 0 ? Math.round((okDocs / totalDocs) * 100) : 0;
          const isExpanded = expandedDocs[o.kurs_id];

          return (
            <Card key={o.kurs_id} className={`${theme.bg} ${theme.border} border-2`} data-testid={`ordner-${o.kurs_id}`}>
              <CardContent className="p-5">
                {/* Kopf */}
                <div className="flex items-start justify-between mb-3">
                  <FolderOpen className={theme.text} size={26} />
                  <Badge className={`${theme.pill} text-white font-mono text-[10px] tracking-wide`}>
                    {o.massnahmenummer}
                  </Badge>
                </div>
                <h2 className={`text-lg font-semibold ${theme.text} leading-tight`} style={{fontFamily: 'Fraunces, serif'}}>{o.name}</h2>
                <p className="text-xs text-[#78716c] mt-1">{o.ue} UE · AZAV-zertifiziert</p>

                {/* Statistik */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className={`${theme.bg} bg-white/60 rounded p-3 border ${theme.border}`}>
                    <div className="flex items-center gap-1.5 text-xs text-[#78716c]"><Users size={12}/> BG-Teilnehmer</div>
                    <div className={`text-2xl font-semibold mt-1 ${theme.text}`} style={{fontFamily: 'Fraunces, serif'}}>{o.teilnehmer_anzahl}</div>
                  </div>
                  <div className={`${theme.bg} bg-white/60 rounded p-3 border ${theme.border}`}>
                    <div className="flex items-center gap-1.5 text-xs text-[#78716c]"><FileCheck size={12}/> Dokumente</div>
                    <div className={`text-2xl font-semibold mt-1 ${theme.text}`} style={{fontFamily: 'Fraunces, serif'}}>{okDocs}/{totalDocs}</div>
                    <div className="text-[10px] text-[#78716c]">{docPct} % vollständig</div>
                  </div>
                </div>

                {/* Aktion: TN zuordnen */}
                <Button
                  size="sm"
                  className={`${theme.accent} text-white w-full mt-4 hover:opacity-90`}
                  onClick={() => openZuordnen(o)}
                  data-testid={`ordner-zuordnen-${o.kurs_id}`}
                >
                  <UserPlus size={14} className="mr-1.5" /> BG-Teilnehmer zuordnen
                </Button>

                {/* Aktion: Anwesenheits-Log-Export für FKS */}
                <Button
                  size="sm"
                  variant="outline"
                  className={`${theme.border} ${theme.text} w-full mt-2 bg-white/70 hover:bg-white`}
                  onClick={() => openLogExport(o)}
                  data-testid={`ordner-log-export-${o.kurs_id}`}
                >
                  <FileText size={14} className="mr-1.5" /> Anwesenheits-Auszug (FKS)
                </Button>

                {/* TN-Liste */}
                {o.teilnehmer.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[#78716c] mb-1.5" style={{fontFamily: 'Oswald, sans-serif'}}>Zugeordnete TN</div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {o.teilnehmer.map(tn => (
                        <div key={tn.id} className="text-xs bg-white/70 rounded px-2 py-1 flex justify-between items-center">
                          <span className="truncate">{tn.name}</span>
                          {tn.kurs_start && <span className="text-[10px] text-[#78716c] ml-2">{new Date(tn.kurs_start).toLocaleDateString('de-DE')}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Doc-Checkliste (Toggle) */}
                <button
                  className="mt-4 w-full flex items-center justify-between text-xs font-medium text-[#78716c] hover:text-[#3D3835] transition"
                  onClick={() => setExpandedDocs(prev => ({...prev, [o.kurs_id]: !prev[o.kurs_id]}))}
                  data-testid={`ordner-docs-toggle-${o.kurs_id}`}
                >
                  <span className="flex items-center gap-1"><FileCheck size={12}/> Pflichtdokumente prüfen</span>
                  {isExpanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                </button>

                {isExpanded && (
                  <div className="mt-3 max-h-64 overflow-y-auto space-y-1 text-xs">
                    {o.docs_status.kurs.length > 0 && (
                      <>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#B79A5C] mt-2 mb-1" style={{fontFamily: 'Oswald, sans-serif'}}>Kurs-spezifisch</div>
                        {o.docs_status.kurs.map(d => (
                          <div key={d.code} className="flex items-center justify-between bg-white/60 rounded px-2 py-1">
                            <div className="flex items-center gap-1.5 min-w-0">
                              {d.vorhanden ? <CheckCircle2 size={11} className="text-emerald-600 shrink-0"/> : <XCircle size={11} className="text-red-500 shrink-0"/>}
                              <span className="font-mono text-[10px] text-[#B79A5C] shrink-0">{d.code}</span>
                              <span className="truncate">{d.name}</span>
                            </div>
                            {d.vorhanden && (
                              <button onClick={() => downloadDoc(d.code)} className="text-[#78716c] hover:text-[#3D3835] shrink-0" title="Herunterladen">
                                <Download size={11}/>
                              </button>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[#B79A5C] mt-3 mb-1" style={{fontFamily: 'Oswald, sans-serif'}}>Träger-übergreifend</div>
                    {o.docs_status.traeger.map(d => (
                      <div key={d.code} className="flex items-center justify-between bg-white/60 rounded px-2 py-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {d.vorhanden ? <CheckCircle2 size={11} className="text-emerald-600 shrink-0"/> : <XCircle size={11} className="text-red-500 shrink-0"/>}
                          <span className="font-mono text-[10px] text-[#B79A5C] shrink-0">{d.code}</span>
                          <span className="truncate">{d.name}</span>
                        </div>
                        {d.vorhanden && (
                          <button onClick={() => downloadDoc(d.code)} className="text-[#78716c] hover:text-[#3D3835] shrink-0" title="Herunterladen">
                            <Download size={11}/>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Unzugeordnete BG-TN */}
      {data.unzugeordnet.length > 0 && (
        <Card className="mt-6 bg-amber-50/70 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase font-semibold text-amber-800 tracking-wider" style={{fontFamily: 'Oswald, sans-serif'}}>Unzugeordnete BG-Teilnehmer</div>
                <div className="text-sm text-amber-900 mt-1">
                  {data.unzugeordnet.length} BG-Teilnehmer sind noch keinem der 3 AZAV-Ordner zugeordnet.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.ordner.map(o => (
                  <Button key={o.kurs_id} size="sm" variant="outline" onClick={() => openZuordnen(o)} data-testid={`unassigned-open-${o.kurs_id}`}>
                    → {o.name}
                  </Button>
                ))}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
              {data.unzugeordnet.slice(0, 12).map(tn => (
                <div key={tn.id} className="bg-white/80 rounded px-2 py-1 text-xs flex justify-between">
                  <span className="truncate font-medium">{tn.name}</span>
                  <span className="text-[10px] text-[#78716c] ml-2 truncate">{tn.kurs_display || "—"}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Zuordnungs-Dialog */}
      <Dialog open={!!zuordnenDialog} onOpenChange={(o) => !o && setZuordnenDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {zuordnenDialog && (
            <>
              <DialogHeader>
                <DialogTitle style={{fontFamily: 'Fraunces, serif'}}>
                  BG-Teilnehmer zuordnen → {zuordnenDialog.ordner.name}
                </DialogTitle>
                <DialogDescription>
                  Wählen Sie einen BG-Teilnehmer, der noch keinem Kurs zugeordnet ist. Der Teilnehmer wird dem Ordner „{zuordnenDialog.ordner.name}" (Maßnahme {zuordnenDialog.ordner.massnahmenummer}) zugewiesen.
                </DialogDescription>
              </DialogHeader>

              <div className="my-3">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716c]"/>
                  <Input
                    placeholder="Name oder E-Mail suchen…"
                    className="pl-8"
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    data-testid="zuordnen-search"
                  />
                </div>
              </div>

              {filteredOhneKurs.length === 0 ? (
                <div className="text-sm text-[#78716c] py-8 text-center">
                  Keine unzugeordneten BG-Teilnehmer {searchQ ? `passend zu „${searchQ}"` : ""} gefunden.
                </div>
              ) : (
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {filteredOhneKurs.map(tn => (
                    <div key={tn.id} className="flex items-center justify-between p-2 border rounded hover:bg-stone-50">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{tn.vorname} {tn.nachname}</div>
                        <div className="text-xs text-[#78716c] truncate">
                          {tn.email || "—"} {tn.kurs_start && <>· Start {new Date(tn.kurs_start).toLocaleDateString('de-DE')}</>}
                          {tn.bg_nummer && <> · BG-Nr. {tn.bg_nummer}</>}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => zuordnen(tn, zuordnenDialog.ordner)}
                        disabled={assigning === tn.id}
                        data-testid={`zuordnen-btn-${tn.id}`}
                      >
                        {assigning === tn.id ? "…" : <>Zuordnen →</>}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setZuordnenDialog(null)}>Schließen</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Anwesenheits-Log-Export-Dialog */}
      <Dialog open={!!logExportDialog} onOpenChange={(o) => !o && !logGenerating && setLogExportDialog(null)}>
        <DialogContent className="max-w-md">
          {logExportDialog && (
            <>
              <DialogHeader>
                <DialogTitle style={{fontFamily: 'Fraunces, serif'}}>
                  Anwesenheits-Auszug für FKS
                </DialogTitle>
                <DialogDescription>
                  Erzeugt ein revisionssicheres PDF gemäß <strong>Umsetzungshinweis 1/2026</strong> der BA
                  für <strong>{logExportDialog.ordner.name}</strong> (Massnahme {logExportDialog.ordner.massnahmenummer}).
                  Alle Kurstage werden <strong>automatisch aus dem festen Schulungsplan</strong> generiert — kein Klassenbuch nötig.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-sm font-medium">Kursstart</Label>
                  <Input type="date" value={logStart} onChange={(e) => setLogStart(e.target.value)} className="mt-1" data-testid="log-export-start"/>
                  <div className="text-xs text-[#8B7E6A] mt-1">Leer lassen → wird aus BG-TN-Daten übernommen</div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Dozent:in-Anzeige (optional)</Label>
                  <Input type="text" placeholder="z. B. J. Müller" value={logDozent} onChange={(e) => setLogDozent(e.target.value)} className="mt-1" data-testid="log-export-dozent"/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Berichts-Von (optional)</Label>
                    <Input type="date" value={logVon} onChange={(e) => setLogVon(e.target.value)} className="mt-1" data-testid="log-export-von"/>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Berichts-Bis (optional)</Label>
                    <Input type="date" value={logBis} onChange={(e) => setLogBis(e.target.value)} className="mt-1" data-testid="log-export-bis"/>
                  </div>
                </div>
                <div className="bg-[#FBF7EE] border-l-2 border-[#B79A5C] px-3 py-2 text-xs text-[#5A5045]">
                  <strong>Enthält:</strong> alle {logExportDialog.ordner.kurs_id === "pflegeassistent_lg1_lg2_bg" ? "32" : logExportDialog.ordner.kurs_id === "ambulanter_pflegeassistent" ? "46" : "66"} Kurstage mit Tag-Nr, Datum, Wochentag, Uhrzeit, Modul, Thema, Lernziel, Dozent:in sowie pro BG-TN den Anwesenheits-Status aus den CRM-Signatur-Logs. Wochenenden &amp; NRW-Feiertage automatisch übersprungen.
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setLogExportDialog(null)} disabled={logGenerating}>Abbrechen</Button>
                <Button onClick={generateAnwesenheitsLog} disabled={logGenerating} data-testid="log-export-generate">
                  {logGenerating ? <><RefreshCw size={14} className="mr-1.5 animate-spin"/>Erzeuge PDF…</> : <><Download size={14} className="mr-1.5"/>PDF erzeugen &amp; herunterladen</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
